import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';

import { authEnabled } from './config';

/**
 * App-wide logout: clears React Query cache + persisted tenant selection,
 * then hands off to Auth0 with an explicit `returnTo`. Consumers should use
 * this instead of calling `useAuth0().logout()` directly so cleanup is
 * always applied.
 *
 * When auth is disabled (no Auth0 config) this is a no-op returning a
 * function that does nothing — callers don't need to branch.
 */
export const useAppLogout = (): (() => void) => {
  // When auth is disabled we still provide a no-op so callers can call
  // it unconditionally. useAuth0 requires an Auth0Provider above, which
  // doesn't exist in dev-bypass mode, so we only invoke it when enabled.
  const queryClient = useQueryClient();

  if (!authEnabled) {
    return () => {
      try {
        window.localStorage.removeItem('activeTenantCode');
      } catch {
        /* ignore */
      }
      queryClient.clear();
    };
  }

  // Enabled path: call useAuth0. Safe because authEnabled is constant per bundle.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { logout } = useAuth0();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useCallback(() => {
    try {
      window.localStorage.removeItem('activeTenantCode');
    } catch {
      /* ignore */
    }
    queryClient.clear();
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout, queryClient]);
};
